/**
 * Wrapper de fetch da API com auto-refresh de JWT.
 * Base '/api' é servida pelo mesmo host (proxy no dev, Fastify estático em prod).
 *
 * Em 401 fora das rotas /auth/: dispara UM refresh compartilhado (single-flight),
 * repete a request uma única vez; se o refresh falhar, derruba a sessão local.
 */
import type { SessionResponse } from '@lira/shared';
import { useAuthStore } from '@/stores/auth';

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
}

/** Rotas de auth não tentam refresh (evita loop) e sempre levam o cookie. */
function isAuthPath(path: string): boolean {
  return path.startsWith('/auth/');
}

async function toApiError(res: Response): Promise<ApiError> {
  let msg = res.statusText || 'Erro na requisição.';
  try {
    const body = (await res.json()) as { error?: string };
    if (body?.error) msg = body.error;
  } catch {
    /* corpo não-JSON */
  }
  return new ApiError(res.status, msg);
}

function rawFetch(path: string, options: RequestOptions): Promise<Response> {
  const { accessToken } = useAuthStore.getState();
  const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData;
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (options.body !== undefined && !isFormData) headers['Content-Type'] = 'application/json';
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;

  return fetch(`/api${path}`, {
    method: options.method ?? 'GET',
    headers,
    body: options.body === undefined ? undefined : isFormData ? (options.body as FormData) : JSON.stringify(options.body),
    credentials: isAuthPath(path) ? 'include' : 'same-origin',
  });
}

async function doRefresh(): Promise<boolean> {
  try {
    const res = await fetch('/api/auth/refresh', {
      method: 'POST',
      headers: { Accept: 'application/json' },
      credentials: 'include',
    });
    if (!res.ok) return false;
    const { accessToken, user } = (await res.json()) as SessionResponse;
    useAuthStore.getState().setSession(accessToken, user);
    return true;
  } catch {
    return false;
  }
}

let refreshInFlight: Promise<boolean> | null = null;

/**
 * Renova a sessão via cookie httpOnly (single-flight: chamadas concorrentes
 * compartilham a mesma promise). `true` = sessão renovada no store.
 * Também usada no bootstrap do App.
 */
export function refreshSession(): Promise<boolean> {
  refreshInFlight ??= doRefresh().finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
}

export async function apiFetch<T>(path: string, options: RequestOptions = {}): Promise<T> {
  let res = await rawFetch(path, options);

  if (res.status === 401 && !isAuthPath(path)) {
    const renewed = await refreshSession();
    if (!renewed) {
      useAuthStore.getState().clear();
      throw new ApiError(401, 'Sessão expirada. Entre novamente.');
    }
    res = await rawFetch(path, options); // repete UMA vez com o token novo
  }

  if (!res.ok) throw await toApiError(res);
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/**
 * GET binário (CSV/arquivos) reusando o token + auto-refresh do apiFetch.
 * Não dá para usar `<a href>` simples porque a rota exige o header Authorization.
 * Retorna o Blob da resposta (o chamador cria o object URL e dispara o download).
 */
export async function apiBlob(path: string): Promise<Blob> {
  const { accessToken } = useAuthStore.getState();
  const build = (): Promise<Response> =>
    fetch(`/api${path}`, {
      method: 'GET',
      headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
      credentials: 'same-origin',
    });

  let res = await build();
  if (res.status === 401 && !isAuthPath(path)) {
    const renewed = await refreshSession();
    if (!renewed) {
      useAuthStore.getState().clear();
      throw new ApiError(401, 'Sessão expirada. Entre novamente.');
    }
    // repete UMA vez com o token novo
    const { accessToken: fresh } = useAuthStore.getState();
    res = await fetch(`/api${path}`, {
      method: 'GET',
      headers: fresh ? { Authorization: `Bearer ${fresh}` } : {},
      credentials: 'same-origin',
    });
  }
  if (!res.ok) throw await toApiError(res);
  return res.blob();
}

export const apiGet = <T>(path: string): Promise<T> => apiFetch<T>(path);

export const apiPost = <T = void>(path: string, body?: unknown): Promise<T> =>
  apiFetch<T>(path, { method: 'POST', body });

/** POST multipart; não define Content-Type para o browser gerar o boundary corretamente. */
export const apiFormPost = <T = void>(path: string, body: FormData): Promise<T> =>
  apiFetch<T>(path, { method: 'POST', body });

export const apiPatch = <T = void>(path: string, body?: unknown): Promise<T> =>
  apiFetch<T>(path, { method: 'PATCH', body });

export const apiPut = <T = void>(path: string, body?: unknown): Promise<T> =>
  apiFetch<T>(path, { method: 'PUT', body });

export const apiDelete = <T = void>(path: string): Promise<T> =>
  apiFetch<T>(path, { method: 'DELETE' });
